import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Alert, CircularProgress, Chip, Divider,
  Accordion, AccordionSummary, AccordionDetails, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PsychologyIcon from '@mui/icons-material/Psychology';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// --- Helpers ---

const SEVERITY_CONFIG = {
  mild:     { color: '#4caf50', bg: '#e8f5e9', label: 'Mild' },
  moderate: { color: '#ff9800', bg: '#fff3e0', label: 'Moderate' },
  severe:   { color: '#f44336', bg: '#ffebee', label: 'Severe' },
};

const INTENSITY_CONFIG = {
  low:    { color: '#66bb6a', bg: '#e8f5e9' },
  medium: { color: '#ffa726', bg: '#fff3e0' },
  high:   { color: '#ef5350', bg: '#ffebee' },
};

const TRIGGER_LABELS = {
  initial:          'Initial Formulation',
  deep_thinking:    'Deep Thinking Update',
  consultation:     'Specialist Consultation',
  assessment:       'Assessment-Driven',
  therapist_review: 'Therapist Review',
  session_progress: 'Session Progress',
  manual:           'Manual Update',
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SeverityChip = ({ severity }) => {
  const config = SEVERITY_CONFIG[severity] || { color: '#9e9e9e', bg: '#f5f5f5', label: severity || 'Unknown' };
  return (
    <Chip
      label={config.label || severity}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        border: `1px solid ${config.color}30`,
      }}
    />
  );
};

const IntensityChip = ({ process: processName, intensity }) => {
  const config = INTENSITY_CONFIG[intensity] || { color: '#9e9e9e', bg: '#f5f5f5' };
  return (
    <Chip
      label={processName}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 500,
        fontSize: '0.75rem',
        border: `1px solid ${config.color}30`,
        mr: 0.5,
        mb: 0.5,
      }}
    />
  );
};

const SectionHeader = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ color: '#37474f', fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}
  >
    {children}
  </Typography>
);

// --- Factor Section (for the 4 Ps text fields) ---

const FactorSection = ({ title, content, defaultExpanded = false }) => {
  if (!content) return null;
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '8px !important',
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
        sx={{
          minHeight: 40,
          '& .MuiAccordionSummary-content': { my: 0.5 },
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a64' }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
        <Typography variant="body2" sx={{ color: '#546e7a', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

// --- Presenting Problems Section ---

const PresentingProblemsSection = ({ problems }) => {
  if (!problems || problems.length === 0) return null;

  const activeProblems = problems.filter(p => p.isActive !== false);
  const inactiveProblems = problems.filter(p => p.isActive === false);

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '8px !important',
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
        sx={{
          minHeight: 40,
          '& .MuiAccordionSummary-content': { my: 0.5 },
          backgroundColor: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a64' }}>
            Presenting Problems
          </Typography>
          <Chip label={`${activeProblems.length} active`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
        {activeProblems.map((problem, idx) => (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1,
              backgroundColor: '#fafbfc',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: '6px',
              borderLeft: `3px solid ${(SEVERITY_CONFIG[problem.severity] || {}).color || '#9e9e9e'}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#37474f', flex: 1 }}>
                {problem.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                <SeverityChip severity={problem.severity} />
                {problem.domain && (
                  <Chip
                    label={problem.domain}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
            {problem.firstIdentifiedAt && (
              <Typography variant="caption" sx={{ color: '#90a4ae' }}>
                First identified: {formatShortDate(problem.firstIdentifiedAt)}
              </Typography>
            )}
          </Paper>
        ))}

        {inactiveProblems.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ color: '#90a4ae', mb: 1, display: 'block' }}>
              Resolved / Inactive
            </Typography>
            {inactiveProblems.map((problem, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{ color: '#b0bec5', textDecoration: 'line-through', mb: 0.5, fontSize: '0.8rem' }}
              >
                {problem.description}
                {problem.severity && ` (${problem.severity})`}
              </Typography>
            ))}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// --- Transdiagnostic Processes Section ---

const TransdiagnosticSection = ({ processes }) => {
  if (!processes || processes.length === 0) return null;

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '8px !important',
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
        sx={{
          minHeight: 40,
          '& .MuiAccordionSummary-content': { my: 0.5 },
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a64' }}>
          Transdiagnostic Processes
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {processes.map((proc, idx) => (
            <Tooltip
              key={idx}
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {proc.process} ({proc.intensity || 'unrated'})
                  </Typography>
                  {proc.notes && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {proc.notes}
                    </Typography>
                  )}
                  {proc.relevantModules && proc.relevantModules.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Modules: {proc.relevantModules.join(', ')}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <span>
                <IntensityChip process={proc.process} intensity={proc.intensity} />
              </span>
            </Tooltip>
          ))}
        </Box>

        {/* Detailed list */}
        {processes.map((proc, idx) => (
          <Box key={idx} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#455a64', fontSize: '0.85rem' }}>
                {proc.process}
              </Typography>
              {proc.intensity && (
                <Typography variant="caption" sx={{ color: (INTENSITY_CONFIG[proc.intensity] || {}).color || '#9e9e9e' }}>
                  ({proc.intensity})
                </Typography>
              )}
            </Box>
            {proc.notes && (
              <Typography variant="body2" sx={{ color: '#78909c', fontSize: '0.8rem', pl: 1 }}>
                {proc.notes}
              </Typography>
            )}
            {proc.relevantModules && proc.relevantModules.length > 0 && (
              <Box sx={{ pl: 1, mt: 0.25 }}>
                {proc.relevantModules.map((mod, mIdx) => (
                  <Chip
                    key={mIdx}
                    label={mod}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 0.5, height: 20, fontSize: '0.65rem', color: '#78909c' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// --- Current Approach Section ---

const CurrentApproachSection = ({ approach }) => {
  if (!approach) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        backgroundColor: '#f3e5f5',
        border: '1px solid #ce93d8',
        borderRadius: '8px',
      }}
    >
      <SectionHeader>Current Therapeutic Approach</SectionHeader>
      {approach.primaryModality && (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#4a148c', mb: 0.5 }}>
          Primary Modality: {approach.primaryModality}
        </Typography>
      )}
      {approach.rationale && (
        <Typography variant="body2" sx={{ color: '#6a1b9a', mb: 1, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {approach.rationale}
        </Typography>
      )}
      {approach.activeModules && approach.activeModules.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 600 }}>
            Active Modules:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {approach.activeModules.map((mod, idx) => (
              <Chip
                key={idx}
                label={mod}
                size="small"
                sx={{
                  backgroundColor: '#e1bee7',
                  color: '#6a1b9a',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24,
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      {approach.lastUpdatedAt && (
        <Typography variant="caption" sx={{ color: '#9c27b0', mt: 1, display: 'block' }}>
          Approach last updated: {formatShortDate(approach.lastUpdatedAt)}
        </Typography>
      )}
    </Paper>
  );
};

// --- Version History Dialog ---

const VersionHistoryDialog = ({ open, onClose, history, loading, error }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon sx={{ color: '#5c6bc0' }} />
        <Typography variant="h6" component="span">Formulation History</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {!loading && !error && (!history || history.length === 0) && (
          <Typography variant="body2" sx={{ color: '#90a4ae', py: 2, textAlign: 'center' }}>
            No previous versions found.
          </Typography>
        )}
        {!loading && history && history.length > 0 && (
          <List disablePadding>
            {history.map((version, idx) => (
              <React.Fragment key={version._id || idx}>
                {idx > 0 && <Divider />}
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#37474f' }}>
                          Version {version.version}
                        </Typography>
                        <Chip
                          label={TRIGGER_LABELS[version.updateTrigger] || version.updateTrigger || 'Unknown'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#90a4ae', display: 'block' }}>
                          {formatDate(version.createdAt)}
                        </Typography>
                        {version.narrativeSummary && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#78909c',
                              mt: 0.5,
                              fontSize: '0.8rem',
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {version.narrativeSummary}
                          </Typography>
                        )}
                        {version.presentingProblems && version.presentingProblems.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', mt: 0.5 }}>
                            {version.presentingProblems.filter(p => p.isActive !== false).length} active presenting problem(s)
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================
// Main Component
// ============================================================

const FormulationView = ({ clientId, token }) => {
  const [formulation, setFormulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const authHeaders = { headers: { 'x-auth-token': token } };

  // Fetch active formulation
  const fetchFormulation = useCallback(async () => {
    if (!clientId || !token) {
      setError('Missing client ID or authentication.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_BASE_URL}/clinical-formulations/client/${clientId}`,
        authHeaders
      );
      setFormulation(res.data || null);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // No formulation exists yet — this is a valid state
        setFormulation(null);
      } else {
        console.error('Error fetching clinical formulation:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load formulation.');
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => {
    fetchFormulation();
  }, [fetchFormulation]);

  // Fetch version history
  const fetchHistory = async () => {
    if (!clientId || !token) return;

    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await axios.get(
        `${API_BASE_URL}/clinical-formulations/client/${clientId}/history`,
        authHeaders
      );
      setHistory(res.data || []);
    } catch (err) {
      console.error('Error fetching formulation history:', err);
      setHistoryError(err.response?.data?.message || err.message || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  // --- Loading state ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={28} sx={{ mr: 1.5 }} />
        <Typography variant="body2" sx={{ color: '#90a4ae' }}>Loading formulation...</Typography>
      </Box>
    );
  }

  // --- Error state ---
  if (error) {
    return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
  }

  // --- Empty state ---
  if (!formulation) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: '#fafbfc',
          border: '1px dashed rgba(0,0,0,0.15)',
          borderRadius: '8px',
        }}
      >
        <PsychologyIcon sx={{ fontSize: 40, color: '#cfd8dc', mb: 1 }} />
        <Typography variant="body1" sx={{ color: '#90a4ae', fontWeight: 500 }}>
          No Clinical Formulation Yet
        </Typography>
        <Typography variant="body2" sx={{ color: '#b0bec5', mt: 0.5 }}>
          A formulation will be generated as the therapeutic process progresses.
        </Typography>
      </Paper>
    );
  }

  // --- Main formulation view ---
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PsychologyIcon sx={{ color: '#5c6bc0', fontSize: '1.4rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238', fontSize: '1.05rem' }}>
            Clinical Formulation
          </Typography>
          <Chip
            label={`v${formulation.version}`}
            size="small"
            sx={{ backgroundColor: '#e8eaf6', color: '#3949ab', fontWeight: 600, height: 22, fontSize: '0.7rem' }}
          />
          {formulation.updateTrigger && (
            <Chip
              label={TRIGGER_LABELS[formulation.updateTrigger] || formulation.updateTrigger}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.65rem', color: '#78909c' }}
            />
          )}
        </Box>
        <Tooltip title="View formulation history">
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
            sx={{ textTransform: 'none', color: '#5c6bc0' }}
          >
            History
          </Button>
        </Tooltip>
      </Box>

      {/* Last updated */}
      <Typography variant="caption" sx={{ color: '#90a4ae', display: 'block', mb: 2 }}>
        Last updated: {formatDate(formulation.updatedAt || formulation.createdAt)}
      </Typography>

      {/* Narrative Summary */}
      {formulation.narrativeSummary && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '8px',
          }}
        >
          <SectionHeader>Narrative Summary</SectionHeader>
          <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {formulation.narrativeSummary}
          </Typography>
        </Paper>
      )}

      {/* Current Approach */}
      <CurrentApproachSection approach={formulation.currentApproach} />

      {/* Presenting Problems */}
      <PresentingProblemsSection problems={formulation.presentingProblems} />

      {/* 4 Ps (Predisposing, Precipitating, Perpetuating, Protective) */}
      <FactorSection
        title="Predisposing Factors"
        content={formulation.predisposingFactors}
        defaultExpanded
      />
      <FactorSection
        title="Precipitating Factors"
        content={formulation.precipitatingFactors}
      />
      <FactorSection
        title="Perpetuating Factors"
        content={formulation.perpetuatingFactors}
      />
      <FactorSection
        title="Protective Factors"
        content={formulation.protectiveFactors}
        defaultExpanded
      />

      {/* Transdiagnostic Processes */}
      <TransdiagnosticSection processes={formulation.transdiagnosticProcesses} />

      {/* Measurement Summary */}
      {formulation.measurementSummary && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 1,
            backgroundColor: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: '8px',
          }}
        >
          <SectionHeader>Measurement Summary</SectionHeader>
          <Typography variant="body2" sx={{ color: '#2e7d32', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {formulation.measurementSummary}
          </Typography>
        </Paper>
      )}

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        loading={historyLoading}
        error={historyError}
      />
    </Box>
  );
};

export default FormulationView;
